import { Button } from '@casavo/base-ui'
import { Body, H2 } from '@casavo/habitat'
import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import Image from 'next/image'

import Chevron from '@/components/Icon/Chevron'
import { useAuthentication } from '@/context/AuthProvider'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import useGoogleAnalyticsIds from '@/hooks/useGoogleAnalyticsIds'
import { sendSnowplowClientSideEvent } from '@/lib/tracking/snowplow/client'
import { eventSchemas, isSnowplowEnabled } from '@/lib/tracking/snowplow/common'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick } from '@/utils/tracking/eventsHandlers'

import { MORTGAGE_BANNER_VALUATION_LINK } from './constants'
import {
  mortgageBannerValuationPageContent,
  mortgageBannerValuationCol1,
  mortgageBannerValuationCta,
} from './styles.css'

export const MortgageBannerValuationPageContent: React.FC<{
  className?: string
  userValuationSessionId: string
}> = ({ className, userValuationSessionId }) => {
  const { t } = useTranslation()
  const { user } = useAuthentication()
  const { googleClientId } = useGoogleAnalyticsIds()

  const {
    publicRuntimeConfig: { imageBasePath },
  } = getConfig()

  return (
    <div className={cn(mortgageBannerValuationPageContent, className)}>
      <div className={mortgageBannerValuationCol1}>
        <div className={sprinkles({ marginBottom: 's' })}>
          <H2 noMargin>{t('suggested:ForYou_Page_Mortgage_CardTitle')}</H2>
        </div>
        <div className={sprinkles({ marginBottom: 'l' })}>
          <Body size="m">{t('suggested:ForYou_Page_Mortgage_Card_Description')}</Body>
        </div>
        <Button
          iconRight
          className={mortgageBannerValuationCta}
          Icon={() => <Chevron />}
          label={t('suggested:ForYou_Page_Mortgage_Card_CTA')}
          onClick={() => {
            handleClick(GTMEventCategory.SuggestedPage, 'MortgageBannerValuationCta', 'PageContent')
            if (isSnowplowEnabled) {
              sendSnowplowClientSideEvent({
                data: {
                  googleClientId: googleClientId,
                  location: 'pageContent',
                  userId: user?.casavo_user_id,
                  valuationSessionId: userValuationSessionId,
                },
                schema: eventSchemas.almostThereMortgageRequestCtaClick,
              })
            }
            window.open(MORTGAGE_BANNER_VALUATION_LINK, '_blank')
          }}
        />
      </div>
      <div className={sprinkles({ display: 'flex' })}>
        <div className={sprinkles({ display: { mobile: 'none', tablet: 'block' }, marginLeft: 'auto' })}>
          <Image
            height={256}
            src={`${imageBasePath}/images/mortgage/mortgage-valuation-banner-desktop.webp`}
            unoptimized={true}
            width={512}
          />
        </div>
        <div
          className={sprinkles({
            display: { mobile: 'block', tablet: 'none' },
            marginBottom: { mobile: 'l', tablet: '2xl' },
            marginLeft: 'auto',
            marginTop: { mobile: 'xl', tablet: '2xl' },
          })}
        >
          <Image
            height={130}
            src={`${imageBasePath}/images/mortgage/mortgage-valuation-banner-mobile.webp`}
            width={312}
          />
        </div>
      </div>
    </div>
  )
}
